import { type ChromeThemeLayer } from './'
import { MotionValue } from 'motion'

/**
 * Types that can be registered as colors.
 */
export type RegistrationColor = string | MotionValue<string> | null

/**
 * Manages the browser chrome theme.
 */
export class ChromeThemeManager {
  private readonly _registrations: Registration[] = []

  private _elements: HTMLMetaElement[] | null = null
  private _scheduledAnimationFrame: number | null = null
  private _previouslySetColor: string | null = null
  private _unsubscribeMotionValue: (() => void) | null = null

  /**
   * Creates a registration with an initial color.
   *
   * @param color
   * @param layer
   */
  register(color: RegistrationColor, layer: ChromeThemeLayer) {
    const registration = new Registration(
      color,
      layer,
      this._onUpdate.bind(this),
    )
    this._registrations.push(registration)
    this._sortRegistrationsByLayer()
    this._onUpdate()
    return registration
  }

  /**
   * Unregisters the specified registration.
   * @param registration
   */
  unregister(registration: Registration) {
    this._registrations.splice(this._registrations.indexOf(registration), 1)
    this._sortRegistrationsByLayer()
    this._onUpdate()
  }

  /**
   * Called when any registration's color is updated, or if a registration
   * is added or removed.
   */
  private _onUpdate() {
    const color = this._registrations.find((r) => r.color)?.color

    this._unsubscribeMotionValue?.()
    this._unsubscribeMotionValue = null
    if (!color) {
      this._clear()
    } else {
      if (color instanceof MotionValue) {
        this._setColor(color.get())
        this._unsubscribeMotionValue = color.on(
          'change',
          this._setColor.bind(this),
        )
      } else {
        this._setColor(color)
      }
    }
  }

  /**
   * Sets the theme color.
   *
   * @param color
   */
  private _setColor(color: string) {
    if (this._previouslySetColor === color) {
      return
    }

    this._raf(() => {
      // Create the elements with the current color if the elements
      // don't already exist.
      if (!this._elements) {
        this._elements = [
          ChromeThemeManager._createThemeMetaElement(color),
          ChromeThemeManager._createThemeMetaElement(
            color,
            '(prefers-color-scheme: light)',
          ),
          ChromeThemeManager._createThemeMetaElement(
            color,
            '(prefers-color-scheme: dark)',
          ),
        ]
        document.head.append(...this._elements)
        return
      }

      // Update the current elements.
      for (const element of this._elements) {
        element.content = color
      }

      this._previouslySetColor = color
    })
  }

  /**
   * Clears the theme color.
   */
  private _clear() {
    this._raf(() => {
      if (!this._elements) {
        return
      }

      for (const element of this._elements) {
        document.head.removeChild(element)
      }
      this._elements = null
      this._previouslySetColor = null
    })
  }

  /**
   * Sort registrations by layer so the first one is the most important.
   * @private
   */
  private _sortRegistrationsByLayer() {
    // Sort in place, descending by layer.
    // That means the first registration will be the one we use if it has
    // a valid color.
    this._registrations.sort((left, right) =>
      left.layer < right.layer ? 1 : left.layer > right.layer ? -1 : 0,
    )
  }

  /**
   * Schedules an animation frame callback.
   * @param callback
   */
  private _raf(callback: () => unknown) {
    if (this._scheduledAnimationFrame) {
      cancelAnimationFrame(this._scheduledAnimationFrame)
    }

    this._scheduledAnimationFrame = requestAnimationFrame(() => {
      this._scheduledAnimationFrame = null
      callback()
    })
  }

  /**
   * Creates a theme metadata element.
   *
   * @param color
   * @param media
   */
  private static _createThemeMetaElement(
    color: string,
    media?: string,
  ): HTMLMetaElement {
    const element = document.createElement('meta')
    element.name = 'theme-color'
    element.content = color
    if (media) {
      element.media = media
    }
    return element
  }
}

/**
 * Chrome theme color registration.
 */
export class Registration {
  constructor(
    public color: RegistrationColor,
    public readonly layer: ChromeThemeLayer,
    private readonly _onUpdate: () => unknown,
  ) {}

  /**
   * Updates the color for this registration.
   *
   * @param color
   */
  update(color: RegistrationColor) {
    if (this.color === color) {
      return
    }

    this.color = color
    this._onUpdate()
  }
}
